/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { TariffList } from 'src/components/Tariff';
import TariffListPlaceholder from 'src/components/Tariff/TariffList/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'tariff.tariff_list.initial_load_error.message',
    defaultMessage: 'An error occurred while loading the tariffs. Please try again.',
  },
});

export const tariffListRouteQuery = graphql`
  query TariffListRoute_Query($uuid: ID!) {
    property(uuid: $uuid) {
      ...TariffList_property
    }
  }
`;

const tariffListRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    params: PropTypes.shape({
      uuid: PropTypes.string,
    }),
  }),
});

/**
 * Renders `TariffList` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} tariff list route content.
 */
function TariffListPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(tariffListRouteQuery, queryRef);
  return <TariffList {...routeProps} {...queryData} />;
}

TariffListPreloadedQueryRenderer.propTypes = {
  routeProps: tariffListRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `TariffList` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} tariff list route content.
 */
function TariffListRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    tariffListRouteQuery,
    routeProps,
    { logLabel: 'TariffListRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={TariffListPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <TariffListPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <TariffList {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

TariffListRouteRenderer.propTypes = {
  routeProps: tariffListRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the TariffList route state supplied by found-relay.
 * Network errors are handled before pending props so the local error boundary mounts.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} TariffList route content or loading state.
 */
export function tariffListRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <TariffListRouteRenderer
        key={routeProps.match.params.uuid}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <TariffListRouteRenderer
      key={props.match.params.uuid}
      routeProps={props}
    />
  );
}

export default TariffListRouteRenderer;
